<template>
  <div>
    <div :class="boxed ? 'form-check' : 'mb-1'">
      <input v-if="boxed"
             :id="boxId"
             type="checkbox"
             :class="['form-check-input', { 'is-invalid': error }]"
             :checked="checked"
             @change="tick(($event.target as HTMLInputElement).checked)"
      >
      <component :is="boxed ? 'label' : 'span'"
                 :class="{ 'form-check-label': boxed }"
                 :for="boxed ? boxId : undefined"
      >
        {{ field.templateOptions.label }}
        <small v-if="required" class="align-middle font-weight-bold text-danger">&nbsp;*</small>
      </component>
      <a v-if="field.templateOptions.textUrl"
         :href="field.templateOptions.textUrl"
         target="_blank"
         rel="noopener noreferrer"
         class="small ml-1"
      >
        {{ $t('Read the full text') }}
      </a>
      <a v-else-if="field.templateOptions.text && !textInline"
         href="#"
         class="small ml-1"
         @click.prevent="showText = true"
      >
        {{ $t('Read the full text') }}
      </a>
      <div v-if="textInline" class="consent-text small mt-1" v-html="field.templateOptions.text" />
    </div>

    <Modal v-if="field.templateOptions.text && !textInline"
           size="lg"
           :title="field.templateOptions.label"
           :show="showText"
           @hide="showText = false"
           @hidePrevented="showText = false"
    >
      <div v-html="field.templateOptions.text" style="max-height: 600px; overflow-y: scroll;" />
    </Modal>

    <div v-for="channel in listedChannels" :key="channel.value" class="form-check form-check-inline">
      <input :id="'registration_' + field.key + '_' + channel.value"
             type="checkbox"
             class="form-check-input"
             :checked="answer.channels?.[channel.value] === true"
             @change="updateChannel(channel.value, ($event.target as HTMLInputElement).checked)"
      >
      <label class="form-check-label" :for="'registration_' + field.key + '_' + channel.value">
        {{ channel.name }}
      </label>
    </div>

    <div v-if="signed" class="row mt-2">
      <div class="col col-md-9 col-lg-7">
        <SignaturePad height="150px" @update:model-value="update({ signature: $event })" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Modal from '@/components/frontend/ui/Modal.vue'
import SignaturePad from '@/components/frontend/signature/SignaturePad.vue'

interface ConsentAnswer {
  version: number
  granted?: boolean
  channels?: Record<string, boolean>
  signature?: string | null
}

interface Props {
  field: {
    key: string
    templateOptions: {
      label: string
      text?: string | null
      textUrl?: string | null
      mode: 'checkbox' | 'signature'
      version: number
      channels?: Array<{ value: string; name: string }>
    }
  }
  modelValue?: ConsentAnswer | null
  required?: boolean
  error?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  required: false,
  error: null,
})

const emit = defineEmits<{ 'update:modelValue': [answer: ConsentAnswer] }>()

const showText = ref(false)

// A marketing consent's words short enough to read at a glance are shown, not hidden behind a link.
const INLINE_TEXT_MAX_LENGTH = 400

const signed = computed(() => props.field.templateOptions.mode === 'signature')
const channels = computed(() => props.field.templateOptions.channels ?? [])
// A marketing consent asked on a single channel is ticked like any other box, in front of its words.
const singleChannel = computed(() => channels.value.length === 1 ? channels.value[0].value : null)
const listedChannels = computed(() => singleChannel.value === null ? channels.value : [])
const boxed = computed(() => !signed.value && listedChannels.value.length === 0)
const boxId = computed(() => 'registration_' + props.field.key + (singleChannel.value ? '_' + singleChannel.value : ''))

const textInline = computed<boolean>(() => {
  const text = props.field.templateOptions.text

  if (channels.value.length === 0 || !text || props.field.templateOptions.textUrl) {
    return false
  }

  const words = new DOMParser().parseFromString(text, 'text/html').body.textContent ?? ''

  return words.trim().length <= INLINE_TEXT_MAX_LENGTH
})

// The version shown travels with the answer, so the server can tell the words changed since.
const answer = computed<ConsentAnswer>(() => ({
  ...props.modelValue,
  version: props.field.templateOptions.version,
}))

const update = (partial: Partial<ConsentAnswer>): void => {
  emit('update:modelValue', { ...answer.value, ...partial })
}

const updateChannel = (channel: string, granted: boolean): void => {
  update({ channels: { ...answer.value.channels, [channel]: granted } })
}

const checked = computed<boolean>(() => singleChannel.value
  ? answer.value.channels?.[singleChannel.value] === true
  : answer.value.granted === true)

const tick = (granted: boolean): void => {
  if (singleChannel.value) {
    updateChannel(singleChannel.value, granted)
  } else {
    update({ granted })
  }
}

// Sent even when left untouched: a box left blank is a refusal, which is an answer too.
onMounted(() => {
  if (props.modelValue?.version !== props.field.templateOptions.version) {
    update({})
  }
})
</script>

<style scoped>
.consent-text :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
